import {
  UIState,
  UIAction,
  UIActionType,
  ModalName,
  HighlightEntry,
} from './types'

// Default statuses (all except Cancelled)
const DEFAULT_STATUSES = [1, 2, 3, 4] // Submitted, Pending, Approved, Approved w/ Conditions

export const HIGHLIGHT_DURATION_MS = 5000

const createInitialModals = (): Record<ModalName, boolean> => ({
  divisionSetup: false,
  divisionClone: false,
  registrationDetails: false,
  adminTeamRegister: false,
  adminPlayerRegister: false,
  bucket: false,
  deleteBucket: false,
  managePlayers: false,
  manageTeams: false,
  approveWithConditions: false,
  cancelRegistration: false,
  paidInPersonAlert: false,
  billingSchedule: false,
})

export const initialUIState: UIState = {
  modals: createInitialModals(),
  // Modal selections
  selectedDivisionModal: null,
  selectedTeam: null,
  selectedPlayer: null,
  bucketToDelete: null,
  // Filter states
  searchText: '',
  selectedStatuses: DEFAULT_STATUSES,
  selectedDivision: 'all',
  onlyBuckets: false,
  onlyDeferredPayments: false,
  allExpanded: false,
  // Expansion states
  expandedTeamRows: new Set<string>(),
  openDivisions: {},
  // Player selection for trading
  selectedPlayerRowKeys: [],
  selectedPlayers: [],
  // Team selection for moving
  selectedTeamRowKeys: [],
  selectedTeamsForMove: [],
  moveTeamsOriginDivisionId: null,
  // Registration details modal
  registrationDetailDefaultTab: '0',
  // Row highlighting
  highlightedRows: new Map<string, HighlightEntry>(),
}

export const uiReducer = (state: UIState, action: UIAction): UIState => {
  switch (action.type) {
    case UIActionType.OPEN_MODAL:
      return {
        ...state,
        modals: {
          ...state.modals,
          [action.payload]: true,
        },
      }

    case UIActionType.CLOSE_MODAL:
      return {
        ...state,
        modals: {
          ...state.modals,
          [action.payload]: false,
        },
      }

    case UIActionType.CLOSE_ALL_MODALS:
      return {
        ...state,
        modals: createInitialModals(),
      }

    case UIActionType.SET_SELECTED_DIVISION_MODAL:
      return {
        ...state,
        selectedDivisionModal: action.payload,
      }

    case UIActionType.SET_SELECTED_TEAM:
      return {
        ...state,
        selectedTeam: action.payload,
      }

    case UIActionType.SET_SELECTED_PLAYER:
      return {
        ...state,
        selectedPlayer: action.payload,
      }

    case UIActionType.SET_BUCKET_TO_DELETE:
      return {
        ...state,
        bucketToDelete: action.payload,
      }

    case UIActionType.SET_SEARCH_TEXT:
      return {
        ...state,
        searchText: action.payload,
      }

    case UIActionType.SET_SELECTED_STATUSES:
      return {
        ...state,
        selectedStatuses: action.payload,
      }

    case UIActionType.TOGGLE_TEAM_ROW: {
      const newExpanded = new Set(state.expandedTeamRows)
      if (newExpanded.has(action.payload)) {
        newExpanded.delete(action.payload)
      } else {
        newExpanded.add(action.payload)
      }
      return {
        ...state,
        expandedTeamRows: newExpanded,
      }
    }

    case UIActionType.EXPAND_ALL_TEAM_ROWS:
      return {
        ...state,
        expandedTeamRows: new Set(action.payload),
      }

    case UIActionType.COLLAPSE_ALL_TEAM_ROWS:
      return {
        ...state,
        expandedTeamRows: new Set<string>(),
      }

    case UIActionType.TOGGLE_DIVISION:
      return {
        ...state,
        openDivisions: {
          ...state.openDivisions,
          [action.payload]: !state.openDivisions[action.payload],
        },
      }

    case UIActionType.SET_OPEN_DIVISIONS:
      return {
        ...state,
        openDivisions: action.payload,
      }

    // Filter actions
    case UIActionType.SET_SELECTED_DIVISION:
      return {
        ...state,
        selectedDivision: action.payload,
      }

    case UIActionType.SET_ONLY_BUCKETS:
      return {
        ...state,
        onlyBuckets: action.payload,
      }

    case UIActionType.SET_ONLY_DEFERRED_PAYMENTS:
      return {
        ...state,
        onlyDeferredPayments: action.payload,
      }

    case UIActionType.SET_ALL_EXPANDED:
      return {
        ...state,
        allExpanded: action.payload,
      }

    // Player selection actions
    case UIActionType.SET_SELECTED_PLAYER_ROW_KEYS:
      return {
        ...state,
        selectedPlayerRowKeys: action.payload,
      }

    case UIActionType.SET_SELECTED_PLAYERS:
      return {
        ...state,
        selectedPlayers: action.payload,
      }

    // Team selection for moving
    case UIActionType.SET_SELECTED_TEAM_ROW_KEYS:
      return {
        ...state,
        selectedTeamRowKeys: action.payload,
      }

    case UIActionType.SET_SELECTED_TEAMS_FOR_MOVE:
      return {
        ...state,
        selectedTeamsForMove: action.payload,
      }

    case UIActionType.SET_MOVE_TEAMS_ORIGIN_DIVISION:
      return {
        ...state,
        moveTeamsOriginDivisionId: action.payload,
      }

    case UIActionType.CLEAR_TEAM_SELECTION:
      return {
        ...state,
        selectedTeamRowKeys: [],
        selectedTeamsForMove: [],
        moveTeamsOriginDivisionId: null,
      }

    // Player selection for trading
    case UIActionType.CLEAR_PLAYER_SELECTION:
      return {
        ...state,
        selectedPlayerRowKeys: [],
        selectedPlayers: [],
      }

    // Registration details modal
    case UIActionType.SET_REGISTRATION_DETAIL_DEFAULT_TAB:
      return {
        ...state,
        registrationDetailDefaultTab: action.payload,
      }

    // Row highlighting
    case UIActionType.ADD_HIGHLIGHTED_ROW: {
      const newHighlighted = new Map(state.highlightedRows)
      newHighlighted.set(action.payload.id, {
        type: action.payload.highlightType,
        addedAt: action.payload.addedAt,
      })
      return {
        ...state,
        highlightedRows: newHighlighted,
      }
    }

    case UIActionType.REMOVE_HIGHLIGHTED_ROW: {
      const newHighlighted = new Map(state.highlightedRows)
      newHighlighted.delete(action.payload)
      return {
        ...state,
        highlightedRows: newHighlighted,
      }
    }

    case UIActionType.CLEAR_EXPIRED_HIGHLIGHTS: {
      const now = action.payload
      let hasExpired = false
      state.highlightedRows.forEach((entry) => {
        if (now - entry.addedAt >= HIGHLIGHT_DURATION_MS) {
          hasExpired = true
        }
      })
      if (!hasExpired) {
        return state
      }
      const newHighlighted = new Map(state.highlightedRows)
      newHighlighted.forEach((entry, id) => {
        if (now - entry.addedAt >= HIGHLIGHT_DURATION_MS) {
          newHighlighted.delete(id)
        }
      })
      return {
        ...state,
        highlightedRows: newHighlighted,
      }
    }

    default:
      return state
  }
}
